<template>
  <v-dialog v-model="dialog" max-width="800px" scrollable>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span>Console Entry Details</span>
        <v-btn icon variant="text" @click="copyToClipboard">
          <v-icon>mdi-content-copy</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text class="log-details">
        <div class="log-meta mb-3">
          <v-chip size="small" class="mr-2">{{ formatTs(consoleEntry.ts) }}</v-chip>
          <v-chip size="small" :color="levelColor" class="mr-2">{{ consoleEntry.level.toUpperCase() }}</v-chip>
        </div>
        
        <div class="console-output">
          <h4>Console Output:</h4>
          <pre class="output-content">{{ formatArgs(consoleEntry.args) }}</pre>
        </div>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="copyToClipboard">
          <v-icon left>mdi-content-copy</v-icon>
          Copy to Clipboard
        </v-btn>
        <v-btn @click="dialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ConsoleEntry } from '@/utils/consoleCapture'

interface Props {
  modelValue: boolean
  consoleEntry: ConsoleEntry
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const levelColor = computed(() => {
  switch (props.consoleEntry.level) {
    case 'error': return 'error'
    case 'warn': return 'warning'
    case 'info': return 'info'
    default: return undefined
  }
})

function formatTs(ts: number) {
  const d = new Date(ts)
  return d.toLocaleTimeString()
}

function formatArgs(args: any[]): string {
  return args.map(a => {
    if (typeof a === 'string') return a
    try { 
      return JSON.stringify(a, null, 2) 
    } catch { 
      return String(a) 
    }
  }).join(' ')
}

function formatConsoleEntry(): string {
  const ts = formatTs(props.consoleEntry.ts)
  const meta = `[${ts}] ${props.consoleEntry.level.toUpperCase()}`
  const output = formatArgs(props.consoleEntry.args)
  return `${meta}\n${output}`
}

async function copyToClipboard() {
  const text = formatConsoleEntry()
  
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // Fallback for older browsers
    const el = document.createElement('textarea')
    el.value = text
    el.style.position = 'fixed'
    el.style.left = '-9999px'
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  }
}
</script>

<style scoped>
.log-details {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
}

.log-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.output-content {
  background: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 4px;
  margin-top: 8px;
  white-space: pre-wrap;
  overflow: auto;
  max-height: 400px;
}

h4 {
  margin-bottom: 4px;
  font-weight: 600;
}
</style>
